/**
 * BondSelector Component
 *
 * Provides UI for selecting glycosidic linkage bonds between sugars
 * Allows selection of:
 * - Anomeric configuration (α/β)
 * - Linkage position (2, 3, 4, 6, etc.)
 */

import React from 'react';
import { Form, Grid, Segment, Label } from 'semantic-ui-react';
import './BondSelector.css';

const BondSelector = ({ sugarIndex, bond, onBondChange, fromSugar, toSugar }) => {
  // Handle anomeric configuration change
  const handleAnomericChange = (e, { value }) => {
    const newBond = bond ? { ...bond } : { anomeric: null, from: 1, to: null };
    newBond.anomeric = value === 'none' ? null : value;

    // If clearing bond completely
    if (value === 'none' && !newBond.to) {
      onBondChange(sugarIndex, null);
    } else {
      onBondChange(sugarIndex, newBond);
    }
  };

  // Handle linkage position change
  const handlePositionChange = (e, { value }) => {
    const newBond = bond ? { ...bond } : { anomeric: null, from: 1, to: null };

    if (value === 'none') {
      newBond.to = null;
      // If clearing bond completely
      if (!newBond.anomeric) {
        onBondChange(sugarIndex, null);
      } else {
        onBondChange(sugarIndex, newBond);
      }
    } else {
      newBond.to = parseInt(value, 10);
      newBond.from = 1; // Always 1 for glycosidic bonds
      onBondChange(sugarIndex, newBond);
    }
  };

  // Options for anomeric configuration
  const anomericOptions = [
    { key: 'none', text: 'Any', value: 'none' },
    { key: 'alpha', text: 'α (alpha)', value: 'a' },
    { key: 'beta', text: 'β (beta)', value: 'b' }
  ];

  // Options for linkage position
  const positionOptions = [
    { key: 'none', text: 'Not specified', value: 'none' },
    { key: '2', text: '2', value: '2' },
    { key: '3', text: '3', value: '3' },
    { key: '4', text: '4', value: '4' },
    { key: '6', text: '6', value: '6' }
  ];

  const currentAnomeric = bond?.anomeric || 'none';
  const currentPosition = bond?.to ? bond.to.toString() : 'none';

  return (
    <div className="bond-selector-container">
      <Segment compact className="bond-selector" size="tiny">
        <Label attached="top" size="tiny" className="bond-selector-label">
          Bond
        </Label>

        <Grid columns={2} className="bond-selector-grid">
          <Grid.Row>
            <Grid.Column width={8}>
              <Form.Select
                fluid
                size="mini"
                label="Type"
                options={anomericOptions}
                value={currentAnomeric}
                onChange={handleAnomericChange}
                className="bond-selector-dropdown"
              />
            </Grid.Column>

            <Grid.Column width={8}>
              <Form.Select
                fluid
                size="mini"
                label="Position"
                options={positionOptions}
                value={currentPosition}
                onChange={handlePositionChange}
                className="bond-selector-dropdown"
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {bond && (bond.anomeric || bond.to) && (
          <div className="bond-preview">
            <Label size="tiny" color="blue">
              {bond.anomeric || ''}
              {bond.to ? `(1→${bond.to})` : ''}
            </Label>
          </div>
        )}
      </Segment>
    </div>
  );
};

export default BondSelector;
